<template>
  <div class="adjustment-draft-detail">
    <KTCodePreview :title="pageTitle">
      <template v-slot:toolbar>
        <b-button size="sm" variant="light" @click="$router.push({ name: 'adjustment-draft-list' })">
          <i class="fas fa-arrow-left mr-1"></i> Danh sách
        </b-button>
        <b-button
          v-if="canIssue"
          size="sm"
          variant="success"
          class="ml-2"
          :disabled="isActioning"
          @click="confirmIssue"
        >
          <i class="fas fa-paper-plane mr-1"></i> Phát hành
        </b-button>
        <b-button
          v-if="draft && ['ISSUE_FAILED', 'WAITING_PROVIDER'].includes(draft.status)"
          size="sm"
          variant="warning"
          class="ml-2"
          :disabled="isActioning || draft.retryCount >= draft.maxRetry"
          v-b-tooltip.hover
          :title="draft.retryCount >= draft.maxRetry ? `Đã đạt giới hạn retry (${draft.maxRetry} lần)` : ''"
          @click="confirmRetry"
        >
          <i class="fas fa-redo mr-1"></i> Thử lại
        </b-button>
        <b-button
          v-if="canDownloadPdf"
          size="sm"
          variant="danger"
          class="ml-2"
          @click="openPdfViewer"
        >
          <i class="fas fa-file-pdf mr-1"></i> Xem PDF
        </b-button>
        <b-button
          v-if="draft && draft.status === 'DRAFT'"
          size="sm"
          variant="danger"
          class="ml-2"
          :disabled="isActioning"
          @click="confirmDelete"
        >
          <i class="fas fa-trash mr-1"></i> Xóa
        </b-button>
      </template>

      <template v-slot:preview>
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="!draft" class="alert alert-warning">
          Không tìm thấy nháp điều chỉnh.
        </div>

        <div v-else>
          <!-- Status + Summary -->
          <div class="d-flex align-items-center mb-4">
            <div class="mr-4">
              <div class="text-muted" style="font-size:0.8rem">Số HĐ điều chỉnh</div>
              <div class="font-weight-bold text-primary">{{ draftPrimaryNo }}</div>
              <div
                v-if="draft.adjustmentInvoiceNo && draft.draftCode"
                class="text-muted"
                style="font-size: 0.8rem"
              >
                {{ draft.draftCode }}
              </div>
            </div>
            <div class="mr-4">
              <div class="text-muted" style="font-size:0.8rem">Trạng thái</div>
              <AdjustmentStatusBadge :status="draft.status" />
            </div>
            <div class="mr-4" v-if="draft.fastSyncStatus">
              <div class="text-muted" style="font-size:0.8rem">FAST Sync</div>
              <AdjustmentStatusBadge :status="draft.fastSyncStatus" />
            </div>
            <div class="mr-4">
              <div class="text-muted" style="font-size:0.8rem">Luồng</div>
              <span class="badge" :class="draft.providerFlow === 'POS' ? 'badge-dark' : 'badge-info'">
                {{ draft.providerFlow }}
              </span>
            </div>
            <div class="mr-4">
              <div class="text-muted" style="font-size:0.8rem">Ngày tạo</div>
              <div>{{ formatDate(draft.createdAt) }}</div>
            </div>
          </div>

          <b-row>
            <!-- Left column -->
            <b-col cols="6">
              <!-- Section: Original Invoice Reference -->
              <b-card class="mb-3" no-body>
                <b-card-header class="py-2"><strong>Tham chiếu hóa đơn gốc</strong></b-card-header>
                <b-card-body class="py-2">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted" style="width:140px">HĐ trả hàng</td>
                      <td class="font-weight-bold">
                        <a
                          v-if="returnBillDetailHref"
                          :href="returnBillDetailHref"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-primary"
                          v-b-tooltip.hover
                          title="Mở hóa đơn trả hàng (tab mới)"
                        >
                          {{ draft.returnBillNumber || draft.returnBillId }}
                          <i class="fas fa-external-link-alt ml-1" style="font-size: 0.75rem"></i>
                        </a>
                        <template v-else>{{ draft.returnBillNumber || draft.returnBillId || '—' }}</template>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">HĐ gốc</td>
                      <td>{{ draft.originalBillNumber || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Mẫu số</td>
                      <td>{{ draft.displayOriginalInvoicePattern || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Ký hiệu</td>
                      <td>{{ draft.displayOriginalInvoiceSerial || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Số HĐ</td>
                      <td class="font-weight-bold">
                        <a
                          v-if="originalEinvoiceDetailHref && draft.displayOriginalInvoiceNo"
                          :href="originalEinvoiceDetailHref"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-primary"
                          v-b-tooltip.hover
                          title="Mở chi tiết HĐĐT gốc (tab mới)"
                        >
                          {{ draft.displayOriginalInvoiceNo }}
                          <i class="fas fa-external-link-alt ml-1" style="font-size: 0.75rem"></i>
                        </a>
                        <template v-else>{{ draft.displayOriginalInvoiceNo || '—' }}</template>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Ngày HĐ</td>
                      <td>{{ formatDate(draft.displayOriginalInvoiceDate) }}</td>
                    </tr>
                  </table>
                </b-card-body>
              </b-card>

              <!-- Section: Issued Invoice Info (if available) -->
              <b-card v-if="isIssued" class="mb-3" no-body>
                <b-card-header class="py-2 bg-success text-white"><strong>Hóa đơn điều chỉnh đã phát hành</strong></b-card-header>
                <b-card-body class="py-2">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted" style="width:140px">Mẫu số</td>
                      <td>{{ draft.adjustmentInvoicePattern || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Ký hiệu</td>
                      <td>{{ draft.adjustmentInvoiceSerial || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Số HĐ</td>
                      <td class="font-weight-bold text-success">{{ draft.adjustmentInvoiceNo || '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Ngày phát hành</td>
                      <td>{{ formatDate(draft.adjustmentInvoiceDate) }}</td>
                    </tr>
                    <tr v-if="draft.adjustmentVerificationCode">
                      <td class="text-muted">Mã xác thực</td>
                      <td><code style="font-size:0.75rem">{{ draft.adjustmentVerificationCode }}</code></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Phát hành lúc</td>
                      <td>{{ formatDate(draft.issuedAt) }}</td>
                    </tr>
                  </table>
                </b-card-body>
              </b-card>

              <!-- Section: Error Info (if failed) -->
              <b-card v-if="draft.errorCode" class="mb-3 border-danger" no-body>
                <b-card-header class="py-2 bg-danger text-white"><strong>Thông tin lỗi</strong></b-card-header>
                <b-card-body class="py-2">
                  <div class="mb-1"><span class="text-muted">Mã lỗi:</span> <code>{{ draft.errorCode }}</code></div>
                  <div class="mb-1"><span class="text-muted">Thông báo:</span> {{ draft.errorMessage }}</div>
                  <div><span class="text-muted">Lần thử:</span> {{ draft.retryCount }} / {{ draft.maxRetry }}</div>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- Right column -->
            <b-col cols="6">
              <!-- Section: Buyer Info -->
              <b-card class="mb-3" no-body>
                <b-card-header class="py-2"><strong>Thông tin người mua</strong></b-card-header>
                <b-card-body class="py-2">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted" style="width:140px">Loại</td>
                      <td>{{ draft.buyerType === 'ENTERPRISE' ? 'Doanh nghiệp' : 'Cá nhân' }}</td>
                    </tr>
                    <tr v-if="draft.buyerLegalName">
                      <td class="text-muted">Tên công ty</td>
                      <td class="font-weight-bold">{{ draft.buyerLegalName }}</td>
                    </tr>
                    <tr v-if="draft.buyerName">
                      <td class="text-muted">Tên người mua</td>
                      <td>{{ draft.buyerName }}</td>
                    </tr>
                    <tr v-if="draft.buyerTaxCode">
                      <td class="text-muted">MST</td>
                      <td>{{ draft.buyerTaxCode }}</td>
                    </tr>
                    <tr v-if="draft.buyerAddress">
                      <td class="text-muted">Địa chỉ</td>
                      <td>{{ draft.buyerAddress }}</td>
                    </tr>
                    <tr v-if="draft.buyerPhone">
                      <td class="text-muted">Điện thoại</td>
                      <td>{{ draft.buyerPhone }}</td>
                    </tr>
                    <tr v-if="draft.buyerEmail">
                      <td class="text-muted">Email</td>
                      <td>{{ draft.buyerEmail }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Tổng trước thuế</td>
                      <td class="text-danger font-weight-bold">{{ convertPrice(draft.amountBeforeTax) }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Tiền thuế</td>
                      <td class="text-danger">{{ convertPrice(draft.taxAmount) }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Tổng cộng</td>
                      <td class="text-danger font-weight-bold">{{ convertPrice(draft.totalAmount) }}</td>
                    </tr>
                  </table>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <!-- Items Table -->
          <b-card class="mb-3" no-body>
            <b-card-header class="py-2"><strong>Danh sách hàng hóa điều chỉnh</strong></b-card-header>
            <b-card-body class="p-0">
              <table class="table table-sm table-bordered mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Tên hàng hóa, dịch vụ</th>
                    <th class="text-center">ĐVT</th>
                    <th class="text-right">SL gốc</th>
                    <th class="text-right">SL ĐC</th>
                    <th class="text-right">Đơn giá</th>
                    <th class="text-right">Chiết khấu ĐC</th>
                    <th class="text-center">Thuế suất</th>
                    <th class="text-right">Thành tiền chưa thuế ĐC</th>
                    <th class="text-right">Tiền thuế ĐC</th>
                    <th class="text-right">Tổng ĐC</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!draft.items || !draft.items.length">
                    <td colspan="10" class="text-center text-muted py-3">Không có dữ liệu</td>
                  </tr>
                  <tr v-for="(it, idx) in draft.items" :key="idx">
                    <td>{{ it.productName }}</td>
                    <td class="text-center">{{ it.unitName || '—' }}</td>
                    <td class="text-right">{{ it.quantityOriginal }}</td>
                    <td class="text-right text-danger">{{ it.quantityAdjustment }}</td>
                    <td class="text-right">{{ convertPrice(it.productPrice) }}</td>
                    <td class="text-right text-danger">{{ convertPrice(it.discountAmount) }}</td>
                    <td class="text-center">{{ formatTaxRate(it.vatRate) }}</td>
                    <td class="text-right text-danger">{{ convertPrice(it.amountBeforeTaxAdjustment) }}</td>
                    <td class="text-right text-danger">{{ convertPrice(it.taxAmountAdjustment) }}</td>
                    <td class="text-right text-danger font-weight-bold">{{ convertPrice(it.totalAmountAdjustment) }}</td>
                  </tr>
                </tbody>
                <tfoot class="font-weight-bold">
                  <tr>
                    <td colspan="7" class="text-right">Tổng:</td>
                    <td class="text-right text-danger">{{ convertPrice(draft.amountBeforeTax) }}</td>
                    <td class="text-right text-danger">{{ convertPrice(draft.taxAmount) }}</td>
                    <td class="text-right text-danger">{{ convertPrice(draft.totalAmount) }}</td>
                  </tr>
                </tfoot>
              </table>
            </b-card-body>
          </b-card>

          <!-- Provider Response Raw Viewer (debug)
          <b-card v-if="draft.providerResponse" class="mb-3" no-body>
            <b-card-header
              class="py-2 d-flex align-items-center"
              style="cursor:pointer"
              @click="showRawResponse = !showRawResponse"
            >
              <strong>Provider Response (raw)</strong>
              <i :class="showRawResponse ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ml-auto"></i>
            </b-card-header>
            <b-collapse :visible="showRawResponse">
              <b-card-body class="p-2">
                <pre class="mb-0" style="font-size:0.75rem;max-height:300px;overflow:auto;background:#f5f5f5;padding:8px;border-radius:4px">{{ JSON.stringify(draft.providerResponse, null, 2) }}</pre>
              </b-card-body>
            </b-collapse>
          </b-card> -->

          <!-- Audit Log -->
          <b-card v-if="draft.auditLogs && draft.auditLogs.length" no-body>
            <b-card-header class="py-2"><strong>Lịch sử thao tác</strong></b-card-header>
            <b-card-body class="py-2">
              <div
                v-for="(log, idx) in draft.auditLogs"
                :key="idx"
                class="audit-entry d-flex align-items-start mb-2 pb-2"
                :class="{ 'border-bottom': idx < draft.auditLogs.length - 1 }"
              >
                <div class="audit-icon mr-2">
                  <i :class="auditIcon(log.action)" class="text-primary"></i>
                </div>
                <div class="flex-grow-1">
                  <div>
                    <strong>{{ auditActionLabel(log.action) }}</strong>
                    <span v-if="log.oldStatus || log.newStatus" class="text-muted ml-2" style="font-size:0.8rem">
                      {{ auditStatusLabel(log.oldStatus) }} → {{ auditStatusLabel(log.newStatus) }}
                    </span>
                    <span v-if="log.actorName" class="text-muted ml-2" style="font-size:0.8rem">
                      bởi {{ log.actorName }}
                    </span>
                  </div>
                </div>
                <div class="text-muted" style="font-size:0.8rem; white-space:nowrap">
                  {{ formatDate(log.createdAt) }}
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>

        <PdfViewerModal ref="pdfViewer" />
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import Swal from 'sweetalert2';
import moment from 'moment';
import AdjustmentStatusBadge from './components/AdjustmentStatusBadge.vue';
import PdfViewerModal from '@/view/components/pdf-viewer/PdfViewerModal.vue';
import { buildAdjustmentDraftPdfViewerConfig } from './utils/adjustmentPdfViewer.utils';
import {
  getAdjustmentAuditActionLabelVi,
  getAdjustmentDraftStatusLabelVi,
} from './constants/adjustmentAuditLabels';
import { ADJUSTMENT_AUDIT_ACTION } from '@/utils/enum';
import { EINVOICE_SALE_TYPE } from '@/utils/einvoice.enum';

const ISSUABLE_STATUSES = ['DRAFT', 'READY_TO_ISSUE'];
const ISSUED_STATUSES = ['ISSUED', 'FAST_SYNCING', 'FAST_SYNCED', 'FAST_SYNC_FAILED'];

export default {
  name: 'AdjustmentDraftDetail',
  components: { KTCodePreview, AdjustmentStatusBadge, PdfViewerModal },
  data() {
    return {
      isLoading: false,
      isActioning: false,
      draft: null,
      showRawResponse: false,
    };
  },
  computed: {
    draftId() {
      return Number(this.$route.params.draftId);
    },
    draftPrimaryNo() {
      if (!this.draft) return '';
      const raw = this.draft.adjustmentInvoiceNo;
      if (raw != null && String(raw).trim() !== '') {
        return String(raw).trim();
      }
      return this.draft.draftCode || `#${this.draft.id}`;
    },
    pageTitle() {
      return this.draft ? `Chi tiết: ${this.draftPrimaryNo}` : 'Chi tiết nháp điều chỉnh';
    },
    canIssue() {
      return this.draft && ISSUABLE_STATUSES.includes(this.draft.status);
    },
    isIssued() {
      return this.draft && ISSUED_STATUSES.includes(this.draft.status);
    },
    canDownloadPdf() {
      return this.draft && this.draft.actions && this.draft.actions.canDownloadPdf;
    },
    returnBillDetailHref() {
      if (!this.draft) return null;
      const billNumber = this.draft.returnBillNumber ?? this.draft.returnBillId;
      if (billNumber == null || String(billNumber).trim() === '') return null;
      const { href } = this.$router.resolve({
        name: 'update-refund-bill',
        query: { billNumber: String(billNumber).trim() },
      });
      return href || null;
    },
    originalEinvoiceSaleType() {
      if (!this.draft) return null;
      const sourceType = String(this.draft.sourceType || '')
        .trim()
        .toUpperCase();
      if (sourceType === 'WHOLESALE') return EINVOICE_SALE_TYPE.WHOLESALE;
      if (sourceType) return EINVOICE_SALE_TYPE.RETAIL;
      if (this.draft.providerFlow === 'PORTAL') return EINVOICE_SALE_TYPE.WHOLESALE;
      if (this.draft.providerFlow === 'POS') return EINVOICE_SALE_TYPE.RETAIL;
      return null;
    },
    originalEinvoiceDetailHref() {
      if (!this.draft) return null;
      const salesInvoiceId = this.draft.originalBillId;
      if (salesInvoiceId == null || String(salesInvoiceId).trim() === '') return null;
      const saleType = this.originalEinvoiceSaleType;
      if (!saleType) return null;
      const { href } = this.$router.resolve({
        name: 'einvoice-detail',
        params: { salesInvoiceId: String(salesInvoiceId).trim() },
        query: { saleType },
      });
      return href || null;
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hóa đơn điện tử', route: 'einvoice-list' },
      { title: 'Nháp điều chỉnh GTGT', route: 'adjustment-draft-list' },
      { title: 'Chi tiết' },
    ]);
    this.fetchDetail();
  },
  methods: {
    convertPrice(value) {
      return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
    },
    formatDate: (d) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'),
    formatTaxRate(taxRate) {
      if (taxRate === -1) return 'KCT';
      if (taxRate === -2) return 'KKK';
      if (taxRate === -9) return 'XK';
      return `${Number(taxRate) || 0}%`;
    },
    auditActionLabel(action) {
      return getAdjustmentAuditActionLabelVi(action);
    },
    auditStatusLabel(status) {
      return getAdjustmentDraftStatusLabelVi(status);
    },
    auditIcon(action) {
      const AA = ADJUSTMENT_AUDIT_ACTION;
      const map = {
        [AA.CREATED]: 'fas fa-plus-circle',
        [AA.STATUS_CHANGED]: 'fas fa-exchange-alt',
        [AA.ISSUED]: 'fas fa-check-double',
        [AA.RETRIED]: 'fas fa-redo',
        [AA.DELETED]: 'fas fa-trash',
        [AA.FAST_SYNC_TRIGGERED]: 'fas fa-sync',
        [AA.FAST_SYNC_SUCCESS]: 'fas fa-cloud-upload-alt',
        [AA.FAST_SYNC_FAILED]: 'fas fa-exclamation-triangle',
        [AA.BUYER_TAX_CODE_EDITED]: 'fas fa-edit',
      };
      return map[action] || 'fas fa-info-circle';
    },
    async fetchDetail() {
      this.isLoading = true;
      try {
        const { data } = await ApiService.get(`v1/invoice-adjustment-drafts/${this.draftId}`);
        this.draft = data.data || data;
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Lỗi tải chi tiết nháp.');
      } finally {
        this.isLoading = false;
      }
    },
    async confirmIssue() {
      const result = await Swal.fire({
        title: 'Phát hành hóa đơn điều chỉnh?',
        html: `<p>Phát hành hoá đơn <strong>${this.draft.returnBillNumber || '#' + this.draft.id}</strong>?</p><p class="text-warning small">Lưu ý! Thao tác này không thể hoàn tác.</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1BC5BD',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Phát hành',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      });
      if (result.value) await this.issueDraft();
    },
    async issueDraft() {
      this.isActioning = true;
      try {
        await ApiService.post(`v1/invoice-adjustment-drafts/${this.draftId}/issue`);
        makeToastSuccess('Phát hành thành công.');
        await this.fetchDetail();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Phát hành thất bại.');
      } finally {
        this.isActioning = false;
      }
    },
    async confirmRetry() {
      this.isActioning = true;
      try {
        await ApiService.post(`v1/invoice-adjustment-drafts/${this.draftId}/retry`);
        makeToastSuccess('Đã gửi lại yêu cầu phát hành.');
        await this.fetchDetail();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Retry thất bại.');
      } finally {
        this.isActioning = false;
      }
    },
    async confirmDelete() {
      const result = await Swal.fire({
        title: 'Xóa nháp?',
        text: `Xóa nháp ${this.draftPrimaryNo}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      });
      if (result.value) {
        this.isActioning = true;
        try {
          await ApiService.delete(`v1/invoice-adjustment-drafts/${this.draftId}`);
          makeToastSuccess('Xóa nháp thành công.');
          this.$router.push({ name: 'adjustment-draft-list' });
        } catch (err) {
          makeToastFaile(err?.response?.data?.message || 'Xóa thất bại.');
        } finally {
          this.isActioning = false;
        }
      }
    },
    async openPdfViewer() {
      try {
        await this.$refs.pdfViewer.open(buildAdjustmentDraftPdfViewerConfig(this.draft));
      } catch (err) {
        makeToastFaile(err?.message || 'Không thể mở PDF hoá đơn');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.audit-entry { font-size: 0.85rem; }
.audit-icon { width: 20px; padding-top: 2px; }
</style>
